<script>
   /*************************************************************/
   /* PlotAcceptance for NIMCA results                           */
   /* ----------------------------                              */
   /* like plotDistance but with extra options.                 */
   /*************************************************************/

   import { Lines, ColormapLegend } from 'svelte-plots-basic/2d';
   import { min,max } from 'mdatools/stat';
   import { csvRow, downloadCSV } from '@mdatools-apps/shared/utils';

   import { getColorGroups, getDistanceCoords, MAX_LABEL_SYMBOLS, MARKERS, MARGINS } from '@mdatools-apps/shared/plots/Defaults.js';
   import PlotSeries from '@mdatools-apps/shared/plots/PlotSeries.svelte';
   import Axes from '@mdatools-apps/shared/plots/Axes.svelte';
   import { plotSize, legendNames } from '@mdatools-apps/shared/plots/layout.js';

   let {
      params,                        // JSON with main plot parameters
      callbacks,                     // JSON with all callback methods
      props,                         // JSON with main properties (model, results, dataset)
      selected,                      // ID of selected object
   } = $props();


   // get the parameters needed for the plot
   // get the parameters needed for the plot
   const data = $derived(props && props.data ? props.data : null);
   const res = $derived(props && props.res ? props.res : null);
   const resobj = $derived(res && params.resID ? res[params.resID] : null);
   const dataset = $derived(res && params.resID ? data[params.resID] : null);
   const model = $derived(props && props.model ? props.model : null);

   // constant plot parameters
   const LINE_COLOR = '#909090';    // color of the limits lines
   const fileName = 'plot-acceptance'; // file name for the plot


   // compute coordinates and labels
   let oCrit = $derived(model && model.oCrit ? model.oCrit : 0);
   let eCrit = $derived(model && model.eCrit ? model.eCrit : 0);

   let xc = $derived(resobj.objindices.map(v => v + 1));
   let yc = $derived(getDistanceCoords(resobj.outcomes.f, 1, 1, params.transType));
   let labels = $derived(resobj.objlabels.map(v => v.slice(0, MAX_LABEL_SYMBOLS)));


   // derive plot parameters
   let title = 'Full distance';
   let xLabel = 'Objects';
   let yLabel = $derived(params.transType === 'log' ? 'Full distance, log(1 + f)' : 'Full distance, f');

   // ranges of values to define coordinate limits
   let xRange = $state(), yRange = $state();

   // reactive expression to compute colorby parameters
   let col = $derived(getColorGroups(dataset, resobj, params.colorBy, params.colorByVar, params.resID, params.colorByObj));

   // the drawn size of the plot, for the class names cut to fit their legend cells
   let size = $state([0, 0]);
   const legendLabels = $derived(col?.byClass ? legendNames(col.labels, size, MARGINS, true) : col?.labels);

   let ind = $derived(params.colorByObj ? params.colorByObj.ind : null);

   // the axis range has to come from the same array the points are drawn from. 'ind' holds
   // positions inside the outlier-free result while 'xc' holds the rows of the original dataset,
   // so taking the range straight from 'ind' measured the axis in the wrong space. the asymmetry
   // below is deliberate: the extra unit before the first point is a margin, and it also keeps a
   // subset of one object from producing 'xMin === xMax'
   const visibleX = $derived(ind?.length ? ind.map(i => xc[i]) : xc);
   let xMax = $derived(max(visibleX));
   let xMin = $derived(min(visibleX) - 1);
   let dX = $derived((xMax - xMin) * 0.025)
   let limX = $derived([xMin - dX, xMax + dX]);

   let ols = $derived({
      x: limX,
      y: params.transType === 'log' ? [Math.log(1 + oCrit), Math.log(1 + oCrit)] : [oCrit, oCrit]
   });

   let els = $derived({
      x: limX,
      y: params.transType === 'log' ? [Math.log(1 + eCrit), Math.log(1 + eCrit)] : [eCrit, eCrit]
   });


   let limY = $derived.by(() => {
      if (!ols || !yRange) return [0, 1];
      const yMaxTot = Math.max(yRange[1], ols.y[0]);
      return [-0.02 * (yMaxTot - yRange[0]), (1.05 + 0.10 * (params.colorBy !== 'none') + 0.05 * (params.showLabels)) * yMaxTot];
   });


   // method for creating CSV version of the plot data
   function downloadCSVCallback() {
      // 'data' is a map keyed by result id: the first set's separator
      const { sep, dec } = Object.values(data)[0];

      // elements of the first (main) subset
      const n = xc.length;

      let l = resobj.objlabels;
      let y = Array.from(yc.values.v);
      let s = Array(n).fill(params.resID);
      let c = resobj.classes && resobj.classes.length === n ? resobj.classes : Array(n).fill("");
      let r = params.colorByObj ? params.colorByObj.values : Array(n).fill("");

      let out = csvRow(['Set', 'Labels', 'Classes', 'Roles', xLabel, yLabel], sep, dec);
      for (let i = 0; i < n; i++) {
         out += csvRow([s[i], l[i], c[i], r[i], xc[i], y[i]], sep, dec);
      }

      downloadCSV(out, fileName);
   }

</script>

{#if xc && yc}
<!-- 'display: contents' leaves the layout to Axes -->
<div style="display: contents" {@attach plotSize(v => size = v)}>
<Axes {title} {limX} {limY} {xLabel} {yLabel} {downloadCSVCallback} {fileName}>

   <!-- limits  -->
   {#if els }
   <Lines xValues={els.x} yValues={els.y} lineColor={LINE_COLOR} lineType={2} />
   {/if}

   {#if ols && (params.resID !== "test" || (params.subset && params.subset === "members")) && params.resID && params.resID !== 'new'}
   <Lines xValues={ols.x} yValues={ols.y} lineColor={LINE_COLOR} lineType={3} />
   {/if}

   <!-- data points 1 -->
   <PlotSeries
      labels={labels}
      {selected}
      {callbacks}
      xValues={xc}
      yValues={yc.values}
      plotType='p'
      showLabels={params.showLabels}
      marker={MARKERS[params.resID]}
      colors={col.colors}
      filled={col.filled}
      {ind}
      bind:xRange={xRange}
      bind:yRange={yRange}
   />

   <!-- color by legend-->
   {#if col && col.showBar}
   <ColormapLegend show={col.breaks !== null} colmap={col.map} breaks={col.breaks} labels={legendLabels}/>
   {/if}
</Axes>
</div>
{/if}
